import { invalidateInventory } from '../collection/inventoryInvalidation';
import { useEffect, useRef, useState } from 'react';
import { InventoryAuthority } from '@qkindex/contracts';
import { logger } from '../../services/logger';
import { Item } from '../../domain/item';
import {
  BulkItemUpdates,
  bulkOperationService,
  DIRECT_BULK_LIMIT_MESSAGE,
  MAX_DIRECT_BULK_ITEMS,
} from '../../services/bulkOperationService';
import { processImage } from '../../services/imageProcessing';
import { useFeedback } from '../feedback/FeedbackProvider';
import { validateKeywords } from '../../domain/keywords';
import { createOperationId } from '../../domain/operationId';

export const appendKeywords = (current: string, addition: string) =>
  validateKeywords(current ? `${current} ${addition}` : addition);

interface PendingImageIntent {
  key: string;
  file: File;
  operationId: string;
  prepared?: Blob;
  preparation?: Promise<Blob | undefined>;
  inFlight?: Promise<void>;
}

export interface BulkActionTransport {
  updateItems(
    items: Array<{ id: string; updates: BulkItemUpdates }>,
    operationId: string,
  ): Promise<unknown>;
  applyImage(
    itemIds: string[],
    image: Blob,
    operationId: string,
  ): Promise<unknown>;
}

export function useBulkActions({
  authority,
  transport,
  scopeKey,
  items,
  selectedIds,
  onComplete,
  onMovedToBin,
}: {
  authority?: InventoryAuthority;
  transport?: BulkActionTransport;
  scopeKey?: string;
  items: Item[];
  selectedIds: Set<string>;
  onComplete: () => void;
  onMovedToBin?: (items: Item[]) => void;
}) {
  const updateItems = (
    updates: Array<{ id: string; updates: BulkItemUpdates }>,
    operationId: string,
  ) => {
    if (transport) return transport.updateItems(updates, operationId);
    if (!authority) throw new Error('Inventory authority is required.');
    return bulkOperationService.updateItems(authority, updates, operationId);
  };
  const applyImage = (ids: string[], image: Blob, operationId: string) => {
    if (transport) return transport.applyImage(ids, image, operationId);
    if (!authority) throw new Error('Inventory authority is required.');
    return bulkOperationService.applyImage(authority, ids, image, operationId);
  };
  const invalidate = () => {
    if (authority && !transport)
      invalidateInventory({ ...authority, resources: ['items'] });
  };
  const { notify, confirm } = useFeedback();
  const [processingImage, setProcessingImage] = useState(false);
  const [imageProgress, setImageProgress] = useState(0);
  const [binning, setBinning] = useState(false);
  const binningRef = useRef(false);
  const pendingOperations = useRef(new Map<string, string>());
  const pendingImage = useRef<PendingImageIntent | undefined>(undefined);
  const selected = () => items.filter((item) => selectedIds.has(item.id));
  const sortedSelectedIds = () => [...selectedIds].sort();
  const selectedById = () =>
    selected().sort((left, right) =>
      left.id < right.id ? -1 : left.id > right.id ? 1 : 0,
    );
  const selectionIsSupported = () => {
    if (selectedIds.size <= MAX_DIRECT_BULK_ITEMS) return true;
    notify(DIRECT_BULK_LIMIT_MESSAGE, 'error');
    return false;
  };
  const execute = async (
    action: () => Promise<void>,
    failureMessage: string,
  ) => {
    try {
      await action();
    } catch (error) {
      logger.error(failureMessage, error);
      notify(
        (error as { code?: string })?.code === 'mutation-pending'
          ? (error as Error).message
          : failureMessage,
        'error',
      );
    }
  };
  const operationFor = (key: string) => {
    const existing = pendingOperations.current.get(key);
    if (existing) return existing;
    const operationId = createOperationId();
    pendingOperations.current.set(key, operationId);
    return operationId;
  };
  const completeOperation = (key: string) =>
    pendingOperations.current.delete(key);
  useEffect(() => {
    pendingOperations.current.clear();
    pendingImage.current = undefined;
    setProcessingImage(false);
    setImageProgress(0);
  }, [
    authority?.collectionId,
    authority?.compatibilityMode,
    authority?.userId,
    scopeKey,
  ]);

  return {
    processingImage,
    imageProgress,
    binning,
    async moveToBin() {
      if (!selectionIsSupported()) return;
      if (binningRef.current) return;
      if (
        !selectedIds.size ||
        !(await confirm(
          `Move ${selectedIds.size} selected item(s) to the bin?`,
        ))
      )
        return;
      binningRef.current = true;
      setBinning(true);
      try {
        await execute(async () => {
          const movedItems = selected();
          const updates = sortedSelectedIds().map((id) => ({
            id,
            updates: { deleted: true },
          }));
          const key = JSON.stringify(['bin', authority?.collectionId, updates]);
          await updateItems(updates, operationFor(key));
          completeOperation(key);
          onMovedToBin?.(movedItems);
          invalidate();
          onComplete();
        }, 'Selected items could not be moved to the bin.');
      } finally {
        binningRef.current = false;
        setBinning(false);
      }
    },
    async appendKeywords(addition: string) {
      if (!selectionIsSupported()) return;
      await execute(async () => {
        const updates = selectedById().map((item) => ({
          id: item.id,
          updates: { keywords: appendKeywords(item.keywords, addition) },
        }));
        const key = JSON.stringify([
          'append-keywords',
          authority?.collectionId,
          updates,
        ]);
        await updateItems(updates, operationFor(key));
        completeOperation(key);
        invalidate();
        onComplete();
      }, 'Keywords could not be appended.');
    },
    async replaceKeywords(find: RegExp, replacement: string) {
      if (!selectionIsSupported()) return;
      await execute(async () => {
        const updates = selectedById().map((item) => ({
          id: item.id,
          updates: {
            keywords: validateKeywords(
              item.keywords.replace(find, replacement),
            ),
          },
        }));
        const key = JSON.stringify([
          'replace-keywords',
          authority?.collectionId,
          updates,
        ]);
        await updateItems(updates, operationFor(key));
        completeOperation(key);
        invalidate();
        onComplete();
      }, 'Keywords could not be replaced.');
    },
    async applyImage(file: File) {
      if (!selectionIsSupported()) return;
      const itemIds = sortedSelectedIds();
      const key = JSON.stringify([
        'image',
        authority?.collectionId,
        itemIds,
        file.name,
        file.size,
        file.type,
        file.lastModified,
      ]);
      let intent = pendingImage.current;
      if (intent?.key !== key || intent.file !== file) {
        intent = { key, file, operationId: createOperationId() };
        pendingImage.current = intent;
      }
      if (!intent.inFlight) {
        const activeIntent = intent;
        setProcessingImage(true);
        setImageProgress(0);
        activeIntent.inFlight = (async () => {
          try {
            if (!activeIntent.prepared) {
              if (!activeIntent.preparation) {
                activeIntent.preparation = (async () => {
                  const image = await processImage(file, {
                    onProgress: (value) => {
                      if (pendingImage.current === activeIntent) {
                        setImageProgress(Math.round(value));
                      }
                    },
                  });
                  if (pendingImage.current !== activeIntent) return undefined;
                  return image;
                })();
              }
              try {
                activeIntent.prepared = await activeIntent.preparation;
              } catch (error) {
                activeIntent.preparation = undefined;
                throw error;
              }
            }
            if (pendingImage.current !== activeIntent || !activeIntent.prepared)
              return;
            await applyImage(
              itemIds,
              activeIntent.prepared,
              activeIntent.operationId,
            );
            if (pendingImage.current !== activeIntent) return;
            pendingImage.current = undefined;
            invalidate();
            onComplete();
          } catch (error) {
            if (pendingImage.current === activeIntent) {
              activeIntent.inFlight = undefined;
              logger.error('Bulk image update failed.', error);
              notify(
                'The image could not be applied to the selected items.',
                'error',
              );
            }
          } finally {
            if (
              pendingImage.current === activeIntent ||
              pendingImage.current === undefined
            ) {
              setProcessingImage(false);
              setImageProgress(0);
            }
          }
        })();
      }
      await intent.inFlight;
    },
  };
}
